import {
  ANNUAL_REVIEW_MISSION_STEPS,
  formatMissionStepLabel,
  tryGetMissionStep,
  type AnnualReviewMissionStepKey,
  type MissionStepOwnerRole,
} from "./annualReviewMission";
import {
  emptyWorkloadMatrixFromSteps,
  missionStepByKey,
  missionStepsFromSnapshot,
  resolveMatrixRowKey,
  type MissionMatrixStep,
} from "./missionMatrix";
import type { MatrixSnapshot } from "@/services/api/agentic-v2/types";
import {
  BUCKET_STATUS_LABELS,
  MILESTONE_STATUS_LABELS,
  type CoverageMatrixCellDetail,
} from "./matrixCellDetail";
import type { HomeTask, HomeTaskCategory } from "./types";

export type MatrixBucket = "done" | "due" | "chasing" | "stale";

export type MatrixViewMode = "task" | "client";

export type MatrixMilestoneState = "none" | "done" | "chasing" | "stale" | "due" | "decide";

export type MatrixCell = Record<MatrixBucket, number>;

export type WorkloadMatrix = Record<string, MatrixCell>;

export type MatrixFilter = {
  missionStepKey?: string;
  clientName?: string;
  bucket?: MatrixBucket;
};

export type MatrixRow = {
  key: string;
  label: string;
  rowId: string;
};

export type MatrixColumnDef = {
  key: string;
  label: string;
  colId: string;
  bucket?: MatrixBucket;
  fullLabel?: string;
};

export type MatrixMilestoneCell = {
  kind: "milestone";
  state: MatrixMilestoneState;
  taskId?: string;
  ownerRole?: MissionStepOwnerRole;
  showAssignee?: boolean;
  assigneeSeed?: string;
  assigneeIsAgent?: boolean;
  detail?: CoverageMatrixCellDetail;
};

export type MatrixCountCell = {
  kind: "count";
  value: number;
  bucket: MatrixBucket;
  detail?: CoverageMatrixCellDetail;
};

export type MatrixCellContent = MatrixMilestoneCell | MatrixCountCell;

export type CoverageMatrixView = {
  mode: MatrixViewMode;
  rows: MatrixRow[];
  columns: MatrixColumnDef[];
  cells: Record<string, Record<string, MatrixCellContent>>;
};

export const MATRIX_BUCKETS: MatrixBucket[] = ["done", "due", "chasing", "stale"];

export const MATRIX_BUCKET_LABELS: Record<MatrixBucket, string> = {
  done: "Done",
  due: "Due",
  chasing: "Chasing",
  stale: "Stale",
};

/** Mirror API `_derive_matrix_bucket` thresholds (hours). */
export const MATRIX_STALE_NEEDS_REVIEW_HOURS = 336; // 14d — human queue neglected
export const MATRIX_STALE_WORKING_HOURS = 72; // 3d — agent stuck
export const MATRIX_STALE_DEFAULT_HOURS = 48; // 2d — other active statuses

/** Matrix row for tasks that do not map to the annual-review mission template. */
export const OTHER_WORK_MATRIX_KEY = "other_work";

export const OTHER_WORK_MATRIX_STEP = {
  key: OTHER_WORK_MATRIX_KEY,
  stageKey: "other",
  label: "Other",
  fullLabel: "Other agent & ops work",
  ownerRole: "paraplanner" as MissionStepOwnerRole,
};

/** Empty matrix with one row per mission step (Saltus annual-review fallback). */
export function emptyWorkloadMatrix(): WorkloadMatrix {
  return emptyWorkloadMatrixFromSteps(ANNUAL_REVIEW_MISSION_STEPS);
}

const LEGACY_CATEGORY_TO_STEP: Partial<
  Record<HomeTaskCategory, AnnualReviewMissionStepKey>
> = {
  letter: "draft_short_letter",
  crm_updates: "propose_crm_updates",
  handover_notes: "draft_handover_notes",
  narrative_review: "review_extractions",
};

/** Resolve matrix row / mission step from API enrichment or legacy task category. */
export function resolveMissionStepKey(
  task: HomeTask,
  missionSteps?: MissionMatrixStep[],
): string | undefined {
  return resolveMatrixRowKey(task, missionSteps);
}

export type TaskAssignee = {
  name: string;
  role: MissionStepOwnerRole;
  isAgent: boolean;
};

/** Resolve the mission-step owner shown on task queue cards and workspaces. */
export function getTaskAssignee(
  task: HomeTask,
  missionSteps?: MissionMatrixStep[],
): TaskAssignee | null {
  const stepKey = resolveMissionStepKey(task);
  if (!stepKey) return null;

  const steps = missionSteps ?? ANNUAL_REVIEW_MISSION_STEPS;
  const knownAnnualStep = tryGetMissionStep(stepKey as AnnualReviewMissionStepKey);
  const step = knownAnnualStep ?? missionStepByKey(steps, stepKey);
  const { ownerRole } = step;
  return {
    name: assigneeDisplayNameForTask(task, ownerRole),
    role: ownerRole,
    isAgent: ownerRole === "agent",
  };
}

function assigneeDisplayNameForTask(
  task: HomeTask,
  ownerRole: MissionStepOwnerRole,
): string {
  if (ownerRole === "agent") return "Ani";
  if (ownerRole === "adviser") return task.adviserName ?? "Adviser";
  if (ownerRole === "administrator") return "Administrator";
  return "You";
}

/** Infer workflow bucket for an active (non-completed) task. */
export function inferTaskBucket(task: HomeTask): Exclude<MatrixBucket, "done"> {
  if (
    task.matrixBucket &&
    task.matrixBucket !== "done"
  ) {
    return task.matrixBucket;
  }
  if (task.blockingSomeone) return "chasing";
  if (task.status === "needs_review") {
    if (task.ageHours >= MATRIX_STALE_NEEDS_REVIEW_HOURS) return "stale";
    return "due";
  }
  if (task.status === "working") {
    if (task.ageHours >= MATRIX_STALE_WORKING_HOURS) return "stale";
    return "due";
  }
  if (task.ageHours >= MATRIX_STALE_DEFAULT_HOURS) return "stale";
  if (task.slaMinutesRemaining < 120) return "chasing";
  return "due";
}

export function matrixRowId(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

function emptyCells(
  rows: MatrixRow[],
  columns: MatrixColumnDef[],
  fill: (column: MatrixColumnDef) => MatrixCellContent,
): Record<string, Record<string, MatrixCellContent>> {
  const cells: Record<string, Record<string, MatrixCellContent>> = {};
  for (const row of rows) {
    cells[row.key] = {};
    for (const column of columns) {
      cells[row.key][column.key] = fill(column);
    }
  }
  return cells;
}

function statusColumns(): MatrixColumnDef[] {
  return MATRIX_BUCKETS.map((bucket) => ({
    key: bucket,
    label: MATRIX_BUCKET_LABELS[bucket],
    colId: bucket,
    bucket,
  }));
}

function missionStepColumns(steps: MissionMatrixStep[]): MatrixColumnDef[] {
  return steps.map((step) => ({
    key: step.key,
    label: step.label,
    colId: step.key,
    fullLabel: step.fullLabel,
  }));
}

function urgencyRank(task: HomeTask): number {
  if (task.completed) return 100;
  const bucket = inferTaskBucket(task);
  const bucketOrder = { chasing: 0, due: 1, stale: 2 } as const;
  return bucketOrder[bucket] * 10 + (task.hitlType === "decide" ? 0 : 1);
}

function assigneeSeedForTask(
  task: HomeTask,
  ownerRole: MissionStepOwnerRole,
): string {
  if (ownerRole === "agent") return "Ani";
  if (ownerRole === "adviser") return task.adviserName ?? "Adviser";
  if (ownerRole === "administrator") return "Admin";
  return task.clientName;
}

function shouldShowAssignee(
  ownerRole: MissionStepOwnerRole,
  state: MatrixMilestoneState,
): boolean {
  if (state === "none" || state === "done") return false;
  return ownerRole !== "paraplanner";
}

function milestoneDetail(
  step: MissionMatrixStep,
  clientName: string,
  state: MatrixMilestoneState,
  task: HomeTask | undefined,
  showAssignee: boolean,
): CoverageMatrixCellDetail {
  const assigneeName =
    showAssignee && task ? assigneeSeedForTask(task, step.ownerRole) : undefined;

  return {
    title: step.fullLabel,
    clientName,
    statusLabel: MILESTONE_STATUS_LABELS[state],
    taskTitle: task?.title,
    assigneeName,
    assigneeRole: showAssignee ? step.ownerRole : undefined,
    assigneeIsAgent: showAssignee && step.ownerRole === "agent",
    canMessage: showAssignee,
    messagePlaceholder: assigneeName
      ? `Where are we on ${step.label.toLowerCase()} for ${clientName}?`
      : undefined,
  };
}

function milestoneForTasks(
  tasks: HomeTask[],
  step: MissionMatrixStep,
  clientName: string,
): MatrixMilestoneCell {
  const ownerRole = step.ownerRole;

  if (tasks.length === 0) {
    return {
      kind: "milestone",
      state: "none",
      ownerRole,
      detail: milestoneDetail(step, clientName, "none", undefined, false),
    };
  }
  if (tasks.every((task) => task.completed)) {
    return {
      kind: "milestone",
      state: "done",
      taskId: tasks[0]?.id,
      ownerRole,
      detail: milestoneDetail(step, clientName, "done", tasks[0], false),
    };
  }

  const active = [...tasks]
    .filter((task) => !task.completed)
    .sort((a, b) => urgencyRank(a) - urgencyRank(b));
  const task = active[0];
  if (!task) {
    return {
      kind: "milestone",
      state: "none",
      ownerRole,
      detail: milestoneDetail(step, clientName, "none", undefined, false),
    };
  }

  let state: MatrixMilestoneState;
  if (task.hitlType === "decide") {
    state = "decide";
  } else {
    state = inferTaskBucket(task);
  }

  const showAssignee = shouldShowAssignee(ownerRole, state);
  return {
    kind: "milestone",
    state,
    taskId: task.id,
    ownerRole,
    showAssignee,
    assigneeSeed: showAssignee ? assigneeSeedForTask(task, ownerRole) : undefined,
    assigneeIsAgent: showAssignee && ownerRole === "agent",
    detail: milestoneDetail(step, clientName, state, task, showAssignee),
  };
}

function countCellDetail(
  step: MissionMatrixStep,
  bucket: MatrixBucket,
  value: number,
): CoverageMatrixCellDetail {
  const bucketLabel = MATRIX_BUCKET_LABELS[bucket];
  return {
    title: `${step.fullLabel} · ${bucketLabel}`,
    statusLabel:
      value === 0
        ? `No ${bucket === "done" ? "cleared" : "active"} tasks`
        : `${value} task${value === 1 ? "" : "s"} · ${BUCKET_STATUS_LABELS[bucket]}`,
  };
}

export function sumMatrixCells(matrix: Record<string, MatrixCell>): MatrixCell {
  const total: MatrixCell = { done: 0, due: 0, chasing: 0, stale: 0 };
  for (const row of Object.values(matrix)) {
    for (const bucket of MATRIX_BUCKETS) {
      total[bucket] += row[bucket] ?? 0;
    }
  }
  return total;
}

export function completionPercent(matrix: Record<string, MatrixCell>): number {
  const totals = sumMatrixCells(matrix);
  const all = MATRIX_BUCKETS.reduce((sum, b) => sum + totals[b], 0);
  if (all === 0) return 100;
  return Math.round((totals.done / all) * 100);
}

/** Merge static done counts with live active task buckets. */
export function buildLiveMatrix(
  baseMatrix: WorkloadMatrix,
  activeTasks: HomeTask[],
  missionSteps?: MissionMatrixStep[],
): Record<string, MatrixCell> {
  const live: Record<string, MatrixCell> = JSON.parse(JSON.stringify(baseMatrix)) as Record<
    string,
    MatrixCell
  >;

  for (const stepKey of Object.keys(live)) {
    const row = live[stepKey];
    row.due = 0;
    row.chasing = 0;
    row.stale = 0;
  }

  if (!live[OTHER_WORK_MATRIX_KEY]) {
    live[OTHER_WORK_MATRIX_KEY] = { done: 0, due: 0, chasing: 0, stale: 0 };
  } else {
    live[OTHER_WORK_MATRIX_KEY].due = 0;
    live[OTHER_WORK_MATRIX_KEY].chasing = 0;
    live[OTHER_WORK_MATRIX_KEY].stale = 0;
  }

  for (const task of activeTasks) {
    const stepKey = resolveMatrixRowKey(task, missionSteps) ?? OTHER_WORK_MATRIX_KEY;
    if (!live[stepKey]) {
      live[stepKey] = { done: 0, due: 0, chasing: 0, stale: 0 };
    }
    const row = live[stepKey];
    if (task.completed) {
      row.done += 1;
      continue;
    }
    row[inferTaskBucket(task)] += 1;
  }

  return live;
}

export function buildCoverageMatrixView(
  mode: MatrixViewMode,
  missionSteps: MissionMatrixStep[],
  baseMatrix: WorkloadMatrix,
  tasks: HomeTask[],
): CoverageMatrixView {
  if (mode === "client") {
    const rows = [...new Set(tasks.map((t) => t.clientName))]
      .sort((a, b) => a.localeCompare(b))
      .map((clientName) => ({
        key: clientName,
        label: clientName,
        rowId: matrixRowId(clientName),
      }));
    const columns = missionStepColumns(missionSteps);
    const cells = emptyCells(rows, columns, () => ({
      kind: "milestone",
      state: "none",
    }));

    for (const row of rows) {
      for (const step of missionSteps) {
        const matching = tasks.filter(
          (task) =>
            task.clientName === row.key &&
            resolveMatrixRowKey(task, missionSteps) === step.key,
        );
        cells[row.key][step.key] = milestoneForTasks(matching, step, row.key);
      }
    }

    return { mode, rows, columns, cells };
  }

  const live = buildLiveMatrix(baseMatrix, tasks, missionSteps);
  const rows = missionSteps.map((step) => ({
    key: step.key,
    label: step.label,
    rowId: step.key,
  }));
  const otherTotals = live[OTHER_WORK_MATRIX_KEY];
  if (
    otherTotals &&
    MATRIX_BUCKETS.some((bucket) => otherTotals[bucket] > 0)
  ) {
    rows.push({
      key: OTHER_WORK_MATRIX_KEY,
      label: OTHER_WORK_MATRIX_STEP.label,
      rowId: OTHER_WORK_MATRIX_KEY,
    });
  }
  const columns = statusColumns();
  const cells = emptyCells(rows, columns, (column) => ({
    kind: "count",
    value: 0,
    bucket: column.bucket!,
  }));

  for (const step of missionSteps) {
    for (const bucket of MATRIX_BUCKETS) {
      const value = live[step.key]?.[bucket] ?? 0;
      cells[step.key][bucket] = {
        kind: "count",
        value,
        bucket,
        detail: countCellDetail(step, bucket, value),
      };
    }
  }

  if (rows.some((row) => row.key === OTHER_WORK_MATRIX_KEY)) {
    for (const bucket of MATRIX_BUCKETS) {
      const value = live[OTHER_WORK_MATRIX_KEY]?.[bucket] ?? 0;
      cells[OTHER_WORK_MATRIX_KEY][bucket] = {
        kind: "count",
        value,
        bucket,
        detail: countCellDetail(OTHER_WORK_MATRIX_STEP, bucket, value),
      };
    }
  }

  return { mode: "task", rows, columns, cells };
}

export function buildCoverageMatrixFromSnapshot(
  mode: MatrixViewMode,
  matrix: MatrixSnapshot | null | undefined,
  tasks: HomeTask[],
): CoverageMatrixView {
  const missionSteps = missionStepsFromSnapshot(matrix);
  const baseMatrix = emptyWorkloadMatrixFromSteps(missionSteps);
  return buildCoverageMatrixView(mode, missionSteps, baseMatrix, tasks);
}

export function filterTasksByMatrix(
  tasks: HomeTask[],
  filter: MatrixFilter | null,
  missionSteps?: MissionMatrixStep[],
): HomeTask[] {
  if (!filter) return tasks.filter((t) => !t.completed);

  if (filter.clientName && !filter.missionStepKey && !filter.bucket) {
    return tasks.filter((task) => task.clientName === filter.clientName);
  }

  if (filter.clientName && filter.missionStepKey && !filter.bucket) {
    return tasks.filter(
      (task) =>
        task.clientName === filter.clientName &&
        resolveMatrixRowKey(task, missionSteps) === filter.missionStepKey,
    );
  }

  if (filter.bucket === "done") {
    return tasks.filter((task) => {
      if (!task.completed) return false;
      if (
        filter.missionStepKey &&
        resolveMatrixRowKey(task, missionSteps) !== filter.missionStepKey
      ) {
        return false;
      }
      if (filter.clientName && task.clientName !== filter.clientName) return false;
      return true;
    });
  }

  return tasks.filter((task) => {
    if (task.completed) return false;
    if (filter.missionStepKey === OTHER_WORK_MATRIX_KEY) {
      if (resolveMatrixRowKey(task, missionSteps)) return false;
    } else if (
      filter.missionStepKey &&
      resolveMatrixRowKey(task, missionSteps) !== filter.missionStepKey
    ) {
      return false;
    }
    if (filter.clientName && task.clientName !== filter.clientName) return false;
    if (filter.bucket && inferTaskBucket(task) !== filter.bucket) return false;
    return true;
  });
}

export function matrixFilterLabel(filter: MatrixFilter | null): string | null {
  if (!filter) return null;
  const parts: string[] = [];
  if (filter.clientName) {
    parts.push(filter.clientName);
  }
  if (filter.missionStepKey) {
    parts.push(
      filter.missionStepKey === OTHER_WORK_MATRIX_KEY
        ? OTHER_WORK_MATRIX_STEP.fullLabel
        : formatMissionStepLabel(filter.missionStepKey),
    );
  }
  if (filter.bucket) {
    parts.push(MATRIX_BUCKET_LABELS[filter.bucket]);
  }
  return parts.length > 0 ? parts.join(" · ") : null;
}
